import React from 'react';
import type { Accessory, Reader } from './types';
import {
  PRICE_FOOTNOTE,
  connectivityBadges,
  formatPrice,
  imageSrc,
  isLimited,
  nameOf,
  paymentTypes,
  specRows,
} from './catalog';

// Import Badge and Card from @coupling/ui so the alias resolution works for all sites
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Badge, Card } from '@coupling/ui';

const TagList: React.FC<{ label: string; items: string[] }> = ({ label, items }) =>
  items.length === 0 ? null : (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="text-sm text-neutral-700">{items.join(' · ')}</p>
    </div>
  );

const Price: React.FC<{ value: number; className?: string }> = ({ value, className }) => (
  <p className={className}>
    {/* TODO(#2289): Coupling merchant pricing is not decided; see formatPrice in catalog.ts. */}
    <span className="font-display text-2xl font-bold text-neutral-900">{formatPrice(value)}</span>
    <span className="ml-2 text-xs text-neutral-500">{PRICE_FOOTNOTE}</span>
  </p>
);

const ProductImage: React.FC<{ file: string; alt: string; small?: boolean }> = ({ file, alt, small }) => (
  <div className={`flex items-center justify-center rounded-lg bg-neutral-50 ${small ? 'h-32' : 'h-48'}`}>
    <img src={imageSrc(file)} alt={alt} loading="lazy" className="max-h-full max-w-full object-contain p-3" />
  </div>
);

export const ReaderCard: React.FC<{ reader: Reader }> = ({ reader }) => {
  const specs = specRows(reader.specs);
  return (
    <Card id={reader.id} padding="md" className="flex h-full scroll-mt-28 flex-col gap-4">
      <ProductImage file={reader.image_file} alt={reader.name} />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="font-display text-xl font-semibold text-neutral-900">{reader.name}</h3>
        {isLimited(reader) && <Badge variant="warning" size="sm">Limited availability</Badge>}
      </div>
      <Price value={reader.price_cents} />
      <p className="text-neutral-700">{reader.tagline ?? reader.description}</p>
      {reader.status_note && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-neutral-800" role="note">
          {reader.status_note}
        </p>
      )}
      <div className="flex flex-wrap gap-2" aria-label="Connectivity">
        {connectivityBadges(reader).map((badge) => (
          <Badge key={badge} variant={badge === 'Offline mode' ? 'success' : 'info'} size="sm">
            {badge}
          </Badge>
        ))}
      </div>
      <TagList label="Payment types" items={paymentTypes} />
      <TagList label="Use cases" items={reader.use_cases} />
      <TagList label="In the box" items={reader.in_the_box ?? []} />
      {(reader.compatible_accessories ?? []).length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">Compatible accessories</p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {reader.compatible_accessories!.map((id) => (
              <li key={id}>
                <a href={`#${id}`} className="text-brand-primary underline-offset-2 hover:underline">
                  {nameOf(id)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {specs.length > 0 && (
        <details className="mt-auto rounded-lg border border-neutral-200 px-4 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-neutral-900">Specs</summary>
          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            {specs.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt className="text-neutral-500">{label}</dt>
                <dd className="text-neutral-800">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      )}
    </Card>
  );
};

export const AccessoryCard: React.FC<{ accessory: Accessory }> = ({ accessory }) => (
  <Card id={accessory.id} padding="sm" className="flex h-full scroll-mt-28 flex-col gap-3">
    <ProductImage file={accessory.image_file} alt={accessory.name} small />
    <h3 className="font-display text-base font-semibold text-neutral-900">{accessory.name}</h3>
    <Price value={accessory.price_cents} />
    <p className="text-sm text-neutral-700">{accessory.description}</p>
    <div className="mt-auto flex flex-wrap gap-2" aria-label="Works with">
      {accessory.works_with.map((id) => (
        <a key={id} href={`#${id}`}>
          <Badge variant="neutral" size="sm">{nameOf(id)}</Badge>
        </a>
      ))}
    </div>
  </Card>
);
